import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Table,
  IconArrowLeft,
  Currency,
  IconBlock,
  IconSelected,
  Popup,
  ButtonPseudo,
  ButtonPrimary,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  currenciesColumns,
  currenciesColumnsLabels,
} from './variables/currencies';
import formatCurrencies from './helpers/format-currencies';
import useAvailableCurrenciesFunctions from './hooks/use-available-currencies';

import cn from './styles.module.css';

const AvailableCurrencies = ({
  setAvailableCurrenciesVisible,
}: {
  setAvailableCurrenciesVisible: (data: boolean) => void;
}) => {
  const { currencies: currenciesList } = useAppSelector((state) => state.root);

  const { accountId } = useParams();

  const {
    blockPopupVisible,
    enablePopupVisible,
    error,
    enableInProgress,
    disableInProgress,
    isLoading,
    isFetching,
    onEnableBalanceAction,
    onDisableBalanceAction,
    balances,
    setBlockPopupVisible,
    setSelectedBlockCurrency,
    setEnablePopupVisible,
    setSelectedEnableCurrency,
    selectedBlockCurrency,
    setError,
    selectedEnableCurrency,
  } = useAvailableCurrenciesFunctions({ accountId });

  const formattedData = useMemo(() => {
    const data = formatCurrencies(
      currenciesList as Currency[],
      balances,
      setBlockPopupVisible,
      setSelectedBlockCurrency,
      setEnablePopupVisible,
      setSelectedEnableCurrency,
    );

    return data;
  }, [currenciesList, balances]);

  return (
    <div className={cn.availableCurrencies}>
      <button
        type="button"
        className={cn.backButton}
        onClick={() => setAvailableCurrenciesVisible(false)}
      >
        <IconArrowLeft width={13} height={11} />
        Back to Account
      </button>
      <div className={cn.currenciesTableWrapper}>
        <Table
          columns={currenciesColumns}
          data={formattedData || []}
          sort={{}}
          onSort={() => {}}
          filters={{}}
          columnsLabels={currenciesColumnsLabels}
          tableBodyClassName={`${cn.currenciesTable} ${
            isLoading || isFetching ? cn.currenciesTableLoading : ''
          }`}
          tableTheadClassName={cn.currenciesTableThead}
          tableTopClassName={cn.currenciesTableThead}
          onFilterChange={() => {}}
          isLoading={isLoading || isFetching}
        />
      </div>
      <Popup
        title={`Disable the balance in ${
          currenciesList?.find((item) => item.id === selectedBlockCurrency?.id)
            ?.name
        } for the customer?`}
        onCancelClick={() => {
          setBlockPopupVisible(false);
          setSelectedBlockCurrency(undefined);
          setError('');
        }}
        isVisible={blockPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      >
        <>
          {error && <div className={cn.error}>{error}</div>}
          <div className={cn.controls}>
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setBlockPopupVisible(false);
                setSelectedBlockCurrency(undefined);
                setError('');
              }}
              className={cn.closePopupButton}
              bold
            />
            <ButtonPrimary
              text={disableInProgress ? 'Loading...' : 'Disable'}
              icon={<IconBlock className={cn.okButtonBlock} />}
              onClick={onDisableBalanceAction}
              className={cn.okButton}
              bold
            />
          </div>
        </>
      </Popup>
      <Popup
        title={`Enable the balance in ${
          currenciesList?.find((item) => item.id === selectedEnableCurrency?.id)
            ?.name
        } for the customer?`}
        onCancelClick={() => {
          setEnablePopupVisible(false);
          setSelectedEnableCurrency(undefined);
          setError('');
        }}
        isVisible={enablePopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      >
        <>
          {error && <div className={cn.error}>{error}</div>}
          <div className={cn.controls}>
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setEnablePopupVisible(false);
                setSelectedEnableCurrency(undefined);
                setError('');
              }}
              className={cn.closePopupButton}
              bold
            />
            <ButtonPrimary
              text={enableInProgress ? 'Loading...' : 'Enable'}
              icon={<IconSelected width={18} height={18} />}
              onClick={onEnableBalanceAction}
              disabled={enableInProgress}
              className={cn.okButton}
              bold
            />
          </div>
        </>
      </Popup>
    </div>
  );
};

export default AvailableCurrencies;
